import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { useTranslation } from 'react-i18next';
import type { LegacyDashboardClient } from '../../models/LegacyDashboardClient';

interface SgaProductsModalProps {
  client: LegacyDashboardClient | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatNumber = (value: number): string => value.toLocaleString();

export const SgaProductsModal = ({ client, open, onOpenChange }: SgaProductsModalProps) => {
  const { t } = useTranslation('legacyDashboardModule');
  const sga = client?.sga;
  const products = sga?.products ?? [];

  const getStatusLabel = (status: string) => {
    const key = `sga.product_status.${status}`;
    const translated = t(key);
    return translated === key ? status : translated;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl border-border bg-background" data-testid="legacy-dashboard-sga-modal">
        <DialogHeader>
          <DialogTitle>{t('sga.modal.title')}</DialogTitle>
          <DialogDescription>
            {client ? t('sga.modal.description', { name: client.name, legacyId: client.legacy_team_id }) : ''}
          </DialogDescription>
        </DialogHeader>

        {sga ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: t('sga.summary.products_count'), value: formatNumber(sga.products_count) },
                {
                  label: t('sga.summary.products_with_stock_count'),
                  value: formatNumber(sga.products_with_stock_count),
                },
                { label: t('sga.summary.total_stock_available'), value: formatNumber(sga.total_stock_available) },
                { label: t('sga.summary.total_stock_reserved'), value: formatNumber(sga.total_stock_reserved) },
                { label: t('sga.summary.total_stock'), value: formatNumber(sga.total_stock) },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-border bg-card p-3">
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="text-lg font-semibold text-foreground mt-1">{item.value}</p>
                </div>
              ))}
            </div>

            {products.length > 0 ? (
              <div className="max-h-[420px] overflow-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('sga.table.id')}</TableHead>
                      <TableHead>{t('sga.table.name')}</TableHead>
                      <TableHead>{t('sga.table.code')}</TableHead>
                      <TableHead>{t('sga.table.status')}</TableHead>
                      <TableHead className="text-right">{t('sga.table.stock_available')}</TableHead>
                      <TableHead className="text-right">{t('sga.table.stock_reserved')}</TableHead>
                      <TableHead className="text-right">{t('sga.table.stock_total')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.id}</TableCell>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell>{product.code || '—'}</TableCell>
                        <TableCell>{product.status ? getStatusLabel(product.status) : '—'}</TableCell>
                        <TableCell className="text-right">{formatNumber(product.stock_available)}</TableCell>
                        <TableCell className="text-right">{formatNumber(product.stock_reserved)}</TableCell>
                        <TableCell className="text-right">{formatNumber(product.stock_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center">{t('sga.modal.empty_products')}</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center">{t('sga.modal.no_data')}</p>
        )}

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('sga.modal.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
